import { CollabClient, type StoredState, type Ticket, type TicketOptions } from '@flobi/collab-client';
import apiClient from '../services/apiClient';

/**
 * The editor's way into flobi-collab (CONTRACTS §11, §13): one `CollabClient` per open file,
 * a ticket for each room it opens, and `artwork.live.push` for the minutes the collab server
 * cannot be reached. Rooms are named `artwork:<documentId>:root` and
 * `artwork:<documentId>:<pageId>`.
 */

/** `VITE_LIVE_ARTWORK=off` is the kill switch (CONTRACTS §11): single-person editing, saved through `live.push`. */
export const LIVE_ARTWORK = (import.meta.env.VITE_LIVE_ARTWORK as string | undefined) !== 'off';

export const rootRoomName = (documentId: string) => `artwork:${documentId}:root`;
export const pageRoomName = (documentId: string, pageId: string) => `artwork:${documentId}:${pageId}`;

/** What the fleet's envelope says when a call is refused with a 200, as an error the client reads. */
function refused(body: { error?: unknown; statusCode?: unknown; errorCode?: unknown; details?: unknown }): Error {
  const error = new Error(typeof body.error === 'string' ? body.error : 'refused');
  return Object.assign(error, {
    status: typeof body.statusCode === 'number' ? body.statusCode : undefined,
    errorCode: typeof body.errorCode === 'string' ? body.errorCode : undefined,
    details: body.details,
  });
}

const failed = (data: unknown): data is Record<string, unknown> => typeof data === 'object' && data !== null && (data as { success?: unknown }).success === false;

/**
 * `POST /collab/collab.ticket.issue` through the gateway, behind the same sign-in as every other
 * call. The client reads the HTTP status and the body's `errorCode` off whatever is thrown:
 * 401 / 403 / 404 stop a room, anything else is tried again.
 */
export async function issueTicket(room: string, opts?: TicketOptions): Promise<Ticket> {
  const response = await apiClient.post('/collab/collab.ticket.issue', { room, ...(opts?.linkToken ? { linkToken: opts.linkToken } : {}) });
  if (failed(response.data)) throw refused(response.data);
  const ticket = (response.data?.data ?? response.data) as Ticket | undefined;
  if (!ticket?.ticket) throw Object.assign(new Error('no ticket in the answer'), { status: 502 });
  return ticket;
}

/**
 * `artwork.live.push`: what a degraded room (collab unreachable for 10 s, or the kill switch)
 * sends every 3 s. The body is the payload as it is, the gateway puts the person on it.
 */
export async function pushUpdate(_room: string, payload: unknown): Promise<unknown> {
  const response = await apiClient.post('/artwork/artwork.live.push', payload);
  if (failed(response.data)) throw refused(response.data);
  return response.data?.data ?? response.data;
}

/** `artwork.live.undoRun`: takes an agent's (or add-on's) run back. A run past its window answers 410 `run-expired`. */
export async function undoRun(room: string, runId: string): Promise<unknown> {
  const response = await apiClient.post('/artwork/artwork.live.undoRun', { room, runId });
  if (failed(response.data)) throw refused(response.data);
  return response.data?.data ?? response.data;
}

/**
 * `artwork.live.state` (CONTRACTS §21.46): the room's stored state and generation, read through the
 * gateway. The client loads it into the room when this device holds no copy, so a file opens (and
 * can be edited through `live.push`) with the kill switch on or collab unreachable, and a first open
 * paints before the room syncs. A room never stored answers `state: null`.
 */
export async function storedState(room: string): Promise<StoredState | null> {
  const response = await apiClient.post('/artwork/artwork.live.state', { room });
  if (failed(response.data)) throw refused(response.data);
  const answer = (response.data?.data ?? response.data) as StoredState | undefined;
  return answer && typeof answer.generation === 'number' ? answer : null;
}

/**
 * This person's users-back id (`/users/get-me`'s `id`): the id presence carries in every app
 * (CONTRACTS §21.47), so `colorFor` and "this is me" agree across apps, and the id collab's
 * tickets and notices use. Never the Clerk id. Asked once per signed-in person.
 */
const myIds = new Map<string, Promise<string>>();
export function myUserId(clerkId: string): Promise<string> {
  let held = myIds.get(clerkId);
  if (!held) {
    held = apiClient.get('/users/get-me').then((response) => {
      const me = (response.data?.data ?? response.data) as { id?: unknown } | undefined;
      if (typeof me?.id !== 'string' || !me.id) throw new Error('get-me answered no id');
      return me.id;
    });
    held.catch(() => myIds.delete(clerkId));
    myIds.set(clerkId, held);
  }
  return held;
}

export interface Me {
  readonly id: string;
  readonly name: string;
  readonly color: string;
  readonly avatarUrl?: string;
}

/**
 * The two-browser harness reads the page's client from `window.__collab` (CONTRACTS §21.37): in
 * development builds, or with `VITE_COLLAB_TEST_HOOK=1`, and never in a production build.
 */
const TEST_HOOK = import.meta.env.DEV || (import.meta.env.VITE_COLLAB_TEST_HOOK as string | undefined) === '1';

/** The tab's client for one open file. `shards` is the optional `COLLAB_SHARDS` cross-check. */
export function createCollabClient(me: Me): CollabClient {
  const client = new CollabClient({
    issueTicket,
    push: pushUpdate,
    user: { id: me.id, name: me.name, color: me.color, ...(me.avatarUrl ? { avatarUrl: me.avatarUrl } : {}) },
    live: LIVE_ARTWORK,
    shards: (import.meta.env.VITE_COLLAB_SHARDS as string | undefined) || undefined,
  });
  if (TEST_HOOK && typeof window !== 'undefined') (window as unknown as { __collab?: CollabClient }).__collab = client;
  return client;
}
